import React from 'react';
import { Link } from 'react-router';
import { useUser } from '../../contexts/UserContext';
import { LscLogo } from '../components/LscLogo';
import LscLandingProductMock from '../components/landing/LscLandingProductMock';
import LscLandingValuePillars from '../components/landing/LscLandingValuePillars';
import LscLandingHowItWorks from '../components/landing/LscLandingHowItWorks';
import LscLandingComparison from '../components/landing/LscLandingComparison';
import LscLandingBenefits from '../components/landing/LscLandingBenefits';
import {
  LANDING_COMMISSIONER_LANE_CREATE,
  LANDING_COMMISSIONER_LANE_CTA,
  LANDING_COMMISSIONER_LANE_DETAIL,
  LANDING_COMMISSIONER_LANE_TITLE,
  LANDING_CTA_LOGGED_IN,
  LANDING_CTA_LOGGED_OUT,
  LANDING_CTA_SECONDARY,
  LANDING_CTA_VIEW_PRICING,
  LANDING_HEADLINE,
  LANDING_HERO_PRIVACY,
  LANDING_HERO_PRIVACY_LINK,
  LANDING_HOW_IT_WORKS_ID,
  LANDING_MEMBER_LANE_CTA,
  LANDING_MEMBER_LANE_DETAIL,
  LANDING_MEMBER_LANE_SIGN_IN,
  LANDING_MEMBER_LANE_TITLE,
  LANDING_PRICING_DETAIL,
  LANDING_PRICING_HEADLINE,
  LANDING_PRICING_SECTION_ID,
  LANDING_PRICING_SUMMARY,
  LANDING_PRODUCT_MOCK_ID,
  LANDING_SUBHEADLINE,
} from '../landing/landingCopy';
import { lscPath, lscReturnTo } from '../routing';
import { legalPageUrl, isAbsoluteLegalUrl } from '../../legal/legalLinks';
import studioLogo from '../../studio/assets/scoutlytics-studio-horizontal.svg';

const LscLandingPage: React.FC = () => {
  const { user, loading, signInWithTwitch } = useUser();

  const signInForJoin = () => signInWithTwitch(lscReturnTo('/join'));
  const signInForPricing = () => signInWithTwitch(lscReturnTo('/pricing'));

  return (
    <div className="space-y-20 md:space-y-24 py-8 md:py-14 px-4 max-w-6xl mx-auto" data-testid="lsc-landing">
      <section className="relative text-center space-y-8 md:space-y-10" data-testid="lsc-landing-hero">
        <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden" aria-hidden>
          <div className="absolute top-0 left-1/2 h-72 w-[min(100%,42rem)] -translate-x-1/2 rounded-full bg-primary-600/15 blur-3xl" />
          <div className="absolute top-24 left-1/2 h-px w-[min(90%,36rem)] -translate-x-1/2 bg-gradient-to-r from-transparent via-primary-500/40 to-transparent" />
        </div>

        <LscLogo variant="hero" className="mx-auto items-center" />
        <div className="space-y-5">
          <h1 className="text-3xl md:text-5xl lg:text-[3.25rem] font-bold text-white leading-tight tracking-tight">
            {LANDING_HEADLINE}
          </h1>
          <p className="text-lg md:text-xl text-secondary-300 max-w-3xl mx-auto leading-relaxed">
            {LANDING_SUBHEADLINE}
          </p>
          <p className="text-sm text-primary-200/90">{LANDING_PRICING_SUMMARY}</p>
          <p className="text-sm text-secondary-400 max-w-2xl mx-auto" data-testid="lsc-landing-hero-privacy">
            {LANDING_HERO_PRIVACY}{' '}
            {isAbsoluteLegalUrl(legalPageUrl('privacy')) ? (
              <a
                href={legalPageUrl('privacy')}
                className="text-primary-300 hover:text-primary-200 underline underline-offset-2"
                data-testid="lsc-landing-privacy-link"
              >
                {LANDING_HERO_PRIVACY_LINK}
              </a>
            ) : (
              <Link
                to={legalPageUrl('privacy')}
                className="text-primary-300 hover:text-primary-200 underline underline-offset-2"
                data-testid="lsc-landing-privacy-link"
              >
                {LANDING_HERO_PRIVACY_LINK}
              </Link>
            )}
            .
          </p>
        </div>

        {!loading && (
          <div
            className="max-w-4xl mx-auto rounded-xl border border-[#8b5cf6]/35 bg-gradient-to-br from-[#8b5cf6]/15 via-[#6d28d9]/10 to-secondary-900/80 p-5 md:p-6 text-left shadow-[0_0_40px_rgba(139,92,246,0.12)]"
            data-testid="lsc-landing-rtts-feature"
          >
            <div className="flex flex-col md:flex-row md:items-center gap-5 md:gap-6">
              <img
                src={studioLogo}
                alt="Scoutlytics Studio"
                className="h-10 md:h-12 w-auto max-w-[280px] object-contain shrink-0"
                data-testid="lsc-landing-rtts-logo"
              />
              <div className="flex-1 min-w-0 flex flex-col items-center gap-3 text-center">
                <p className="text-sm md:text-base text-[#c4b5fd] leading-relaxed w-full">
                  Turn your Twitch VOD into a highlight reel in ~15–30 minutes — hands off.
                </p>
                <Link
                  to="/studio"
                  className="inline-flex items-center justify-center rounded-md bg-[#6d28d9] hover:bg-[#7c3aed] text-white text-sm font-semibold px-4 py-2.5 no-underline transition-colors"
                  data-testid="lsc-landing-rtts-cta"
                >
                  Cut a Highlight Reel
                </Link>
              </div>
            </div>
          </div>
        )}

        {!loading && (
          <div
            className="grid gap-4 md:grid-cols-2 max-w-4xl mx-auto text-left"
            data-testid="lsc-landing-acquisition-lanes"
          >
            <div className="card space-y-3" data-testid="lsc-landing-member-lane">
              <h2 className="text-lg font-semibold text-white">{LANDING_MEMBER_LANE_TITLE}</h2>
              <p className="text-sm text-secondary-400">{LANDING_MEMBER_LANE_DETAIL}</p>
              <div className="flex flex-wrap gap-2 pt-1">
                {user ? (
                  <Link to={lscPath('/join')} className="btn-primary text-sm" data-testid="lsc-landing-member-join">
                    {LANDING_MEMBER_LANE_CTA}
                  </Link>
                ) : (
                  <button
                    type="button"
                    className="btn-primary text-sm"
                    data-testid="lsc-landing-launch-cta"
                    onClick={signInForJoin}
                  >
                    {LANDING_MEMBER_LANE_SIGN_IN}
                  </button>
                )}
                <Link to={lscPath('/leagues')} className="btn-secondary text-sm">
                  {user ? LANDING_CTA_LOGGED_IN : LANDING_CTA_LOGGED_OUT}
                </Link>
              </div>
            </div>

            <div className="card space-y-3" data-testid="lsc-landing-commissioner-lane">
              <h2 className="text-lg font-semibold text-white">{LANDING_COMMISSIONER_LANE_TITLE}</h2>
              <p className="text-sm text-secondary-400">{LANDING_COMMISSIONER_LANE_DETAIL}</p>
              <div className="flex flex-wrap gap-2 pt-1">
                {user ? (
                  <Link
                    to={lscPath('/leagues/new')}
                    className="btn-primary text-sm"
                    data-testid="lsc-landing-start-setup"
                  >
                    {LANDING_COMMISSIONER_LANE_CREATE}
                  </Link>
                ) : (
                  <button
                    type="button"
                    className="btn-primary text-sm"
                    onClick={signInForPricing}
                    data-testid="lsc-landing-commissioner-sign-in"
                  >
                    {LANDING_COMMISSIONER_LANE_CTA}
                  </button>
                )}
                <Link
                  to={lscPath('/pricing')}
                  className="btn-secondary text-sm"
                  data-testid="lsc-landing-view-pricing"
                >
                  {LANDING_CTA_VIEW_PRICING}
                </Link>
              </div>
            </div>
          </div>
        )}

        {!loading && (
          <a
            href={`#${LANDING_PRODUCT_MOCK_ID}`}
            className="btn-secondary inline-flex"
            data-testid="lsc-landing-see-how-it-works"
          >
            {LANDING_CTA_SECONDARY}
          </a>
        )}

        <p className="text-xs text-secondary-500">
          <a href={`#${LANDING_HOW_IT_WORKS_ID}`} className="text-primary-300 hover:text-primary-200">
            Jump to How It Works
          </a>
          {' · '}
          <a href={`#${LANDING_PRICING_SECTION_ID}`} className="text-primary-300 hover:text-primary-200">
            Jump to pricing
          </a>
        </p>
      </section>

      <LscLandingProductMock />
      <LscLandingValuePillars />
      <LscLandingHowItWorks />
      <LscLandingComparison />
      <LscLandingBenefits />

      <section
        id={LANDING_PRICING_SECTION_ID}
        className="card max-w-2xl mx-auto space-y-4 text-center"
        data-testid="lsc-landing-pricing-section"
      >
        <h2 className="text-2xl font-bold text-white">{LANDING_PRICING_HEADLINE}</h2>
        <p className="text-lg text-primary-200">{LANDING_PRICING_SUMMARY}</p>
        <p className="text-sm text-secondary-400">{LANDING_PRICING_DETAIL}</p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-2">
          <Link to={lscPath('/join')} className="btn-secondary" data-testid="lsc-landing-pricing-join">
            {LANDING_MEMBER_LANE_CTA}
          </Link>
          <Link to={lscPath('/pricing')} className="btn-primary" data-testid="lsc-landing-pricing-cta">
            {LANDING_COMMISSIONER_LANE_CTA}
          </Link>
          {!loading && !user ? (
            <button type="button" className="btn-secondary" onClick={signInForJoin}>
              {LANDING_MEMBER_LANE_SIGN_IN}
            </button>
          ) : null}
        </div>
      </section>
    </div>
  );
};

export default LscLandingPage;
